import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import { formatUSD } from "../../lib/formatters";

const blank = () => ({
  memberId: "",
  startDate: "",
  endDate: "",
  notes: "",
});

// Sum the per-day rate × days for a vacation range, splitting across any
// month boundary so months with 28/30/31 days price each day correctly.
function computeDeduction(member, startDate, endDate) {
  if (!member || !startDate || !endDate || startDate > endDate) {
    return { days: 0, deduction: 0 };
  }
  const monthly = Number(member.monthlySalary) || 0;
  let days = 0;
  let deduction = 0;
  const cur = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = cur.getMonth() + 1;
    const daysInMonth = new Date(y, m, 0).getDate();
    days += 1;
    deduction += monthly / daysInMonth;
    cur.setDate(cur.getDate() + 1);
  }
  return { days, deduction };
}

export default function VacationForm({ open, onClose, onSubmit, initial, team = [] }) {
  const [form, setForm] = useState(() => initial || blank());
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(initial || blank());
    setErrors({});
  }, [open, initial]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const member = useMemo(
    () => team.find((m) => m.id === form.memberId),
    [team, form.memberId]
  );

  const preview = useMemo(
    () => computeDeduction(member, form.startDate, form.endDate),
    [member, form.startDate, form.endDate]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.memberId) errs.memberId = "Pick a member";
    if (!form.startDate) errs.startDate = "Required";
    if (!form.endDate) errs.endDate = "Required";
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      errs.endDate = "End date is before start";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSubmit({ ...form });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit vacation" : "Add vacation"}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Select
          label="Team member *"
          value={form.memberId}
          onChange={(e) => update("memberId", e.target.value)}
          error={errors.memberId}
        >
          <option value="">— Pick a member —</option>
          {team.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}{m.role ? ` (${m.role})` : ""}
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Start date *"
            type="date"
            value={form.startDate}
            onChange={(e) => update("startDate", e.target.value)}
            error={errors.startDate}
          />
          <Input
            label="End date *"
            type="date"
            value={form.endDate}
            onChange={(e) => update("endDate", e.target.value)}
            error={errors.endDate}
          />
        </div>

        {member && preview.days > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800/40 px-3 py-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-amber-800 dark:text-amber-200">
                <span className="font-semibold">{preview.days}</span> day{preview.days === 1 ? "" : "s"} of unpaid leave
              </span>
              <span className="font-semibold text-amber-800 dark:text-amber-200">
                − {formatUSD(preview.deduction)}
              </span>
            </div>
            <div className="text-[11px] text-amber-700/80 dark:text-amber-300/70 mt-0.5">
              Calculated at monthly salary ÷ days in each month covered.
            </div>
          </div>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-brand-slate mb-1">Notes</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Reason or context (optional)"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : "Add vacation"}</Button>
        </div>
      </form>
    </Modal>
  );
}
